import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerOrderDetail } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerOrders } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type DraftActionInput = {
  storeSlug: string;
  orderId: string;
};

async function invalidateOrderCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  input: DraftActionInput,
): Promise<void> {
  await invalidateSellerOrders(queryClient, input.storeSlug);
  await queryClient.invalidateQueries({
    queryKey: queryKeys.seller.orderById(input.storeSlug, input.orderId),
    exact: true,
  });
}

/** Finalize a COD draft into a confirmed order (seller takes it on the buyer's
 * behalf). Returns the updated order. */
export function useConfirmDraftMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DraftActionInput) =>
      apiJsonClient<SellerOrderDetail>(
        endpoints.seller.orderDraftConfirm(input.orderId),
        { method: "POST", headers: { "Content-Type": "application/json" } },
      ),
    onSuccess: (_data, input) => invalidateOrderCaches(queryClient, input),
  });
  return { mutateAsync: mutation.mutateAsync };
}

/** Discard a draft — cancels it and releases its held stock reservations. */
export function useDiscardDraftMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DraftActionInput) =>
      apiJsonClient<{ ok: boolean }>(
        endpoints.seller.orderDraftDiscard(input.orderId),
        { method: "POST", headers: { "Content-Type": "application/json" } },
      ),
    onSuccess: (_data, input) => invalidateOrderCaches(queryClient, input),
  });
  return { mutateAsync: mutation.mutateAsync };
}
